import { z } from 'zod';
import { FixedReaderContextZ } from './fixed-reader-context-schema';

/**
 * クライアント側 NDJSON ストリーミング用 Zod スキーマ。
 *
 * サーバー側 packages/core-domain/src/ai/schema.ts の StreamEvent / Choice 系
 * スキーマと同一形状を維持する複製。
 *
 * なぜ複製か (= 統合しない理由): client-player は monorepo boundary doctrine 上
 * @palimia/core-domain に依存できない (= core-domain は Prisma / OpenAI client 等の
 * server-only runtime を持つため、client bundle への混入を避ける)。よって
 * 「client が core-domain の ChoiceZ を import する」型統合は禁止で、ここで独自
 * declaration を維持する。
 *
 * 同期規律: ChoiceZ / CompleteEventZ などの形状 (field 集合) と各 field の制約
 * (emotion の max(80) 等) を変更する場合は必ず両側 (core-domain/src/ai/schema.ts ↔
 * 本ファイル) を同時に更新すること。drift は両側の lock test で検知する:
 *   - field 集合: "ChoiceZ field-set lock" (Object.keys で field 名を pin)
 *   - 制約: "ChoiceZ constraint lock" (emotion の境界値 80/81 字を pin)
 *   - client: tests/unit/stream-schema.test.ts
 *   - server: packages/core-domain/__tests__/ai/schema.test.ts (field-set) /
 *     schema.choice-emotion.test.ts (constraint)
 * 配信用complete.sceneとcutinはAPI固有の追加契約であり、
 * core-api/src/validators/common.tsと共有wire fixtureで同期する。
 * AI生成用schemaにはこれらのDTOを追加しない。
 * 過去に片側のみ additive 拡張して emotion field が一時消失したバグ
 * (streaming-feature-parity PR #71) の再発防止。
 */

// ──────────────────────────────────────────────
// Segment
// ──────────────────────────────────────────────

export const NarrationSegmentZ = z.object({
  type: z.literal('narration'),
  text: z.string(),
});

export const DialogueSegmentZ = z.object({
  type: z.literal('dialogue'),
  speakerId: z.string(),
  speakerName: z.string(),
  text: z.string(),
  // visual_novel actor variant: 未指定なら Player 側で既存立ち絵へ fallback する。
  actingVariantId: z.string().min(1).max(64).optional(),
  cutin: z.literal(true).optional(),
  cutinImageUrl: z.string().min(1).optional(),
});

export const SegmentZ = z.discriminatedUnion('type', [NarrationSegmentZ, DialogueSegmentZ]);

// ──────────────────────────────────────────────
// Choice
// ──────────────────────────────────────────────

export const ChoiceVisualCueZ = z.object({
  characterId: z.string().min(1),
  actingVariantId: z.string().min(1).max(64),
  previewLayer: z.enum(["standing", "cutin", "none"]).optional(),
  confirmLayer: z.enum(["standing", "cutin", "none"]).optional(),
});

// choice-emotion (Phase 4) / memory-system (Phase 2-1) additive: optional field。
// server が付与して送るが、未指定のときは従来通り。Zod の strip unknown keys 挙動で
// 落ちていた emotion / requiresMemory を明示 optional 化し、streaming 経路の
// complete event 直後 UI でも sync 経路 (DB → API) と同じ表示になるようにする
// (streaming-feature-parity PR #71 Task 4.3 smoke で発覚した UX 差異を解消)。
export const ChoiceZ = z.object({
  id: z.string(),
  text: z.string(),
  // emotion は主人公の内心 1 行 (80 字以内)。server 側 ChoiceZ.emotion.max(80) と同期
  // (= 両側の "ChoiceZ constraint lock" test で境界値を pin)。
  emotion: z.string().max(80).optional(),
  requiresMemory: z.union([z.string(), z.array(z.string())]).optional(),
  visualCue: ChoiceVisualCueZ.optional(),
});

// interactive-deduction (Phase 3-3) additive: complete event に同梱される矛盾配列を
// 受け取るための schema。server 側 Contradiction 型 (core-domain/src/deduction/types.ts)
// と shape parity を保つ。client 側で UI 表示に使う consumer は現時点で無いが、
// stream-schema が strip しないように field を optional で受け入れる (streaming-feature-parity
// PR #71 Task 2.2.1 で server 側は既に同梱済)。
export const ContradictionZ = z.object({
  id: z.string(),
  text: z.string(),
  expectedMemoryKey: z.string(),
  hint: z.string().optional(),
});

// current/action/completeに共通のScene envelope。旧runtimeではcontextを要求しない。
export const SceneResponseZ = z.object({
  id: z.string(),
  segments: z.array(SegmentZ),
  choices: z.array(ChoiceZ),
  isEnding: z.boolean(),
  contradictions: z.array(ContradictionZ).nullable().optional(),
  backgroundLabel: z.string().nullable().optional(),
  fixedContentNodeKey: z.string().nullable().optional(),
  fixedContentVersionId: z.string().nullable().optional(),
  fixedContext: FixedReaderContextZ.optional(),
});

// ──────────────────────────────────────────────
// StreamEvent
// ──────────────────────────────────────────────

export const SegmentEventZ = z.object({
  type: z.literal('segment'),
  data: SegmentZ,
});

export const CompleteEventZ = z.object({
  type: z.literal('complete'),
  data: z.object({
    choices: z.array(ChoiceZ),
    isEnding: z.boolean(),
    backgroundLabel: z.string().nullable().optional(),
    model: z.string().optional(),
    inputTokens: z.number().int().nonnegative().optional(),
    outputTokens: z.number().int().nonnegative().optional(),
    latencyMs: z.number().nonnegative().optional(),
    promptSummary: z.string().optional(),
    // streaming-feature-parity PR #71 Task 2.2.1: server が post-processed contradictions を
    // complete event に同梱するため、client 側 schema も optional で受け入れる (Zod strip 防止)。
    contradictions: z.array(ContradictionZ).optional(),
    scene: SceneResponseZ.optional(),
  }),
});

export const ErrorEventZ = z.object({
  type: z.literal('error'),
  data: z.object({
    message: z.string(),
  }),
});

export const StreamEventZ = z.discriminatedUnion('type', [
  SegmentEventZ,
  CompleteEventZ,
  ErrorEventZ,
]);

// ──────────────────────────────────────────────
// 型エイリアス
// ──────────────────────────────────────────────

export type NarrationSegment = z.infer<typeof NarrationSegmentZ>;
export type DialogueSegment = z.infer<typeof DialogueSegmentZ>;
export type StreamSegment = z.infer<typeof SegmentZ>;
export type ChoiceVisualCue = z.infer<typeof ChoiceVisualCueZ>;
export type StreamChoice = z.infer<typeof ChoiceZ>;
export type StreamContradiction = z.infer<typeof ContradictionZ>;
export type SegmentEvent = z.infer<typeof SegmentEventZ>;
export type CompleteEvent = z.infer<typeof CompleteEventZ>;
export type ErrorEvent = z.infer<typeof ErrorEventZ>;
export type StreamEvent = z.infer<typeof StreamEventZ>;
